import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { chatsApi } from '@/api/chats'
import { useTenantKey } from './useTenantKey'

export function useChats(projectId: string, search?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', search),
    queryFn: () => chatsApi.getAll(projectId, search),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })
}

export function useChatMessages(projectId: string, chatId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId!, 'messages'),
    queryFn: () => chatsApi.getMessages(projectId, chatId!),
    enabled: !!projectId && !!chatId,
    refetchInterval: chatId ? 4_000 : false,
    refetchOnWindowFocus: true,
  })
}

export function useCreateChat(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: () => chatsApi.create(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}

export function useDeleteChat(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (chatId: string) => chatsApi.delete(projectId, chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}

export function useUpdateChatTitle(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ chatId, title }: { chatId: string; title: string }) =>
      chatsApi.updateTitle(projectId, chatId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}
